import React, { useState, useEffect } from 'react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../contexts/LanguageContext'
import nocobaseService from '../services/nocobaseService'
import { formatPlainTextExcerpt } from '../utils/textFormatting.jsx'
import Header from './Header'
import Footer from './Footer'

const InsightsBlogPage = ({ onOpenChat }) => {
  const { lang } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { currentLanguage, changeLanguage } = useLanguage()
  
  // Determine the language to use - prioritize URL lang over context
  const activeLanguage = (lang && (lang === 'ar' || lang === 'en')) ? lang : currentLanguage
  
  // Update language from URL if provided, or redirect to language-specific URL
  useEffect(() => {
    if (lang && (lang === 'ar' || lang === 'en')) {
      // Language is in URL, update context if different
      if (lang !== currentLanguage) {
        changeLanguage(lang)
      }
    } else if (currentLanguage) {
      // No language in URL, redirect to include language (only once)
      navigate(`/${currentLanguage}/insights`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang])
  
  // Update URL when language changes in context
  useEffect(() => {
    if (currentLanguage && lang && lang !== currentLanguage && (lang === 'ar' || lang === 'en')) {
      navigate(`/${currentLanguage}/insights`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage])
  
  // Use activeLanguage for display
  const displayLanguage = activeLanguage
  
  const [insights, setInsights] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedPostType, setSelectedPostType] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        setLoading(true)
        const data = await nocobaseService.getPosts({ status: 'Published' })
        console.log('📰 Loaded posts from NocoBase:', data)
        console.log('📊 Available posts for clicking:', data.map(i => ({
          id: i.id,
          name: i.Name,
          slug: i.slug || i.Name?.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          Post_Type: i.Post_Type
        })))
        setInsights(data)
      } catch (err) {
        console.error('Failed to fetch posts:', err)
        setError('Failed to load posts')
      } finally {
        setLoading(false)
      }
    }

    fetchInsights()
  }, [])

  const getTitle = (insight) => {
    return insight[`title_${displayLanguage}`] || insight.title_en || insight.Name || ''
  }

  const getExcerpt = (insight) => {
    const rawExcerpt = insight[`excerpt_${displayLanguage}`] || insight.excerpt_en || insight.Summary || '';
    return formatPlainTextExcerpt(rawExcerpt, 150);
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return date.toLocaleDateString(displayLanguage === 'ar' ? 'ar-SA' : 'en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    })
  }

  // Post types mapping
  const postTypes = {
    'all': displayLanguage === 'ar' ? 'الكل' : 'All',
    'Articles ': displayLanguage === 'ar' ? 'مقالات' : 'Articles',
    'Events': displayLanguage === 'ar' ? 'فعاليات' : 'Events',
    'Social Media ': displayLanguage === 'ar' ? 'منشورات' : 'Social Media'
  }

  // Get unique post types from insights
  const availablePostTypes = ['all', ...new Set(insights.map(insight => insight.Post_Type).filter(Boolean))]
  
  // Filter insights by Post_Type and search query
  const filteredInsights = insights.filter(insight => {
    // Filter by post type
    const matchesType = selectedPostType === 'all' || insight.Post_Type === selectedPostType
    
    // Filter by search query
    if (!searchQuery.trim()) {
      return matchesType
    }
    
    const query = searchQuery.toLowerCase()
    const title = getTitle(insight).toLowerCase()
    const excerpt = getExcerpt(insight).toLowerCase()
    const content = (insight.content_en || insight.content_ar || '').toLowerCase()
    
    return matchesType && (title.includes(query) || excerpt.includes(query) || content.includes(query))
  })

  const handleInsightClick = (insight) => {
    const insightId = insight.id;
    const url = `/${displayLanguage}/insights/id/${insightId}`
    console.log('🔗 Blog page - Clicking insight:', {
      name: insight.Name,
      id: insightId,
      targetUrl: url
    })
    navigate(url)
    
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }, 100)
  }

  if (loading) {
    return (
      <div className="insights-blog-page" style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column'
      }}>
        <Header onOpenChat={onOpenChat} />
        <section style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)'
        }}>
          <div style={{ fontSize: '18px', color: '#666' }}>
            {t('common.loading')}
          </div>
        </section>
        <div style={{ marginTop: 'auto', paddingTop: '60px' }}>
          <Footer />
        </div>
      </div>
    )
  }

  if (error || !insights || insights.length === 0) {
    return (
      <div className="insights-blog-page" style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column'
      }}>
        <Header onOpenChat={onOpenChat} />
        <section style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)'
        }}>
          <div style={{ fontSize: '18px', color: '#666', textAlign: 'center' }}>
            <p>{t('common.error')} - Unable to load insights</p>
            <Link 
              to="/" 
              style={{
                color: '#1a1a1a',
                textDecoration: 'underline',
                fontSize: '16px'
              }}
            >
              Return to Homepage
            </Link>
          </div>
        </section>
        <div style={{ marginTop: 'auto', paddingTop: '60px' }}>
          <Footer />
        </div>
      </div>
    )
  }

  return (
    <div className="insights-blog-page" style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column'
    }}>
      <Header onOpenChat={onOpenChat} />
      
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        padding: '120px 0 80px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px',
          textAlign: 'center'
        }}>
          {/* Breadcrumb */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '30px'
          }}>
            <Link 
              to="/" 
              style={{
                color: 'rgba(255, 255, 255, 0.8)',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              {displayLanguage === 'ar' ? 'الرئيسية' : 'Home'}
            </Link>
            <span style={{ color: 'rgba(255, 255, 255, 0.5)', margin: '0 15px' }}>/</span>
            <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', fontWeight: '600' }}>
              {displayLanguage === 'ar' ? 'مدونة التحديثات القانونية' : 'Legal Updates Blog'}
            </span>
          </div>

          <h1 style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 'clamp(36px, 6vw, 64px)',
            fontWeight: 700,
            lineHeight: 1.2,
            marginBottom: '20px',
            letterSpacing: '-0.02em'
          }}>
            {displayLanguage === 'ar' ? 'مدونة التحديثات القانونية' : 'Legal Updates Blog'}
          </h1>

          <p style={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 'clamp(18px, 3vw, 22px)',
            fontWeight: 300,
            lineHeight: 1.6,
            color: 'rgba(255, 255, 255, 0.9)',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {displayLanguage === 'ar' ? 
              'اكتشف أحدث التطورات القانونية والتحليلات المتخصصة في القانون السعودي والدولي' :
              'Discover the latest legal developments and expert analysis in Saudi and international law'
            }
          </p>

          <div style={{
            marginTop: '30px',
            fontSize: '16px',
            color: 'rgba(255, 255, 255, 0.8)'
          }}>
            {insights.length} {displayLanguage === 'ar' ? 'مقال متاح' : 'Articles Available'}
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section style={{
        padding: '60px 0',
        background: '#fafafa',
        minHeight: '60vh'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '0 20px',
          display: 'grid',
          gridTemplateColumns: displayLanguage === 'ar' ? '1fr 320px' : '320px 1fr',
          gap: '40px',
          alignItems: 'start'
        }}>
          {/* Sidebar - Search and Filters */}
          <aside style={{
            position: 'sticky',
            top: '100px',
            alignSelf: 'start',
            order: displayLanguage === 'ar' ? 2 : 1,
            gridColumn: displayLanguage === 'ar' ? 2 : 1
          }}>
            <div style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '30px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: '1px solid rgba(0, 0, 0, 0.06)'
            }}>
              {/* Search Bar */}
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#1a1a1a',
                  marginBottom: '15px',
                  textAlign: displayLanguage === 'ar' ? 'right' : 'left'
                }}>
                  {displayLanguage === 'ar' ? 'البحث' : 'Search'}
                </h3>
                <div style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <input
                    type="text"
                    placeholder={displayLanguage === 'ar' ? 'ابحث في المنشورات...' : 'Search posts...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px 45px 12px 15px',
                      fontSize: '15px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '12px',
                      outline: 'none',
                      transition: 'all 0.3s ease',
                      fontFamily: 'Inter, sans-serif',
                      textAlign: displayLanguage === 'ar' ? 'right' : 'left'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#1a1a1a'
                      e.target.style.boxShadow = '0 0 0 3px rgba(26, 26, 26, 0.1)'
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e0e0e0'
                      e.target.style.boxShadow = 'none'
                    }}
                  />
                  <svg 
                    width="18" 
                    height="18" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="#999" 
                    strokeWidth="2"
                    style={{
                      position: 'absolute',
                      right: displayLanguage === 'ar' ? 'auto' : '15px',
                      left: displayLanguage === 'ar' ? '15px' : 'auto',
                      pointerEvents: 'none'
                    }}
                  >
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                </div>
              </div>

              {/* Filters */}
              <div>
                <h3 style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#1a1a1a',
                  marginBottom: '15px',
                  textAlign: displayLanguage === 'ar' ? 'right' : 'left'
                }}>
                  {displayLanguage === 'ar' ? 'التصنيفات' : 'Categories'}
                </h3>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px'
                }}>
                  {availablePostTypes.map((postType) => (
                    <button
                      key={postType}
                      onClick={() => setSelectedPostType(postType)}
                      style={{
                        padding: '12px 18px',
                        borderRadius: '10px',
                        border: '2px solid',
                        borderColor: selectedPostType === postType ? '#1a1a1a' : '#e0e0e0',
                        background: selectedPostType === postType ? '#1a1a1a' : 'transparent',
                        color: selectedPostType === postType ? '#fff' : '#666',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        textTransform: 'capitalize',
                        fontFamily: 'Inter, sans-serif',
                        textAlign: displayLanguage === 'ar' ? 'right' : 'left',
                        width: '100%'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedPostType !== postType) {
                          e.target.style.borderColor = '#1a1a1a'
                          e.target.style.color = '#1a1a1a'
                          e.target.style.backgroundColor = 'rgba(26, 26, 26, 0.05)'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedPostType !== postType) {
                          e.target.style.borderColor = '#e0e0e0'
                          e.target.style.color = '#666'
                          e.target.style.backgroundColor = 'transparent'
                        }
                      }}
                    >
                      {postTypes[postType] || postType}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Results Count */}
              {filteredInsights.length > 0 && (
                <div style={{
                  marginTop: '25px',
                  padding: '15px',
                  background: 'rgba(26, 26, 26, 0.03)',
                  borderRadius: '10px',
                  textAlign: 'center',
                  fontSize: '14px',
                  color: '#666',
                  fontFamily: 'Inter, sans-serif'
                }}>
                  <strong style={{ color: '#1a1a1a' }}>{filteredInsights.length}</strong> {displayLanguage === 'ar' ? 'منشور' : 'post'} {filteredInsights.length === 1 ? '' : displayLanguage === 'ar' ? '' : 's'}
                </div>
              )}
            </div>
          </aside>

          {/* Blog Grid - Main Content */}
          <div style={{
            order: displayLanguage === 'ar' ? 1 : 2,
            gridColumn: displayLanguage === 'ar' ? 1 : 2
          }}>
            {filteredInsights.length > 0 ? (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: '30px'
              }}>
                {filteredInsights.map((insight, index) => (
                  <article
                    key={insight.id}
                    style={{
                      background: '#fff',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      border: '1px solid rgba(0, 0, 0, 0.06)',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleInsightClick(insight);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)'
                      e.currentTarget.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.15)'
                      e.currentTarget.style.borderColor = '#1a1a1a'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)'
                      e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)'
                      e.currentTarget.style.borderColor = 'rgba(0, 0, 0, 0.06)'
                    }}
                  >
                    {/* Image */}
                    <div style={{
                      width: '100%',
                      height: '220px',
                      backgroundColor: '#000000',
                      backgroundImage: `url('${insight.image || insight.background_image || insight.featured_image || '/pic/' + ((index % 4) + 1) + '.jpg'}')`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      position: 'relative'
                    }}>
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.4))'
                      }} />
                      
                      {/* Post Type Badge */}
                      <div style={{
                        position: 'absolute',
                        top: '15px',
                        ...(displayLanguage === 'ar' ? { right: '15px' } : { left: '15px' })
                      }}>
                        <span style={{
                          padding: '6px 14px',
                          background: 'rgba(255, 255, 255, 0.95)',
                          color: '#333',
                          borderRadius: '20px',
                          fontSize: '11px',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                        }}>
                          {postTypes[insight.Post_Type] || insight.Post_Type || 'Article'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div style={{ 
                      padding: '25px',
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      {/* Date */}
                      <div style={{
                        marginBottom: '12px',
                        fontSize: '13px',
                        color: '#888',
                        fontFamily: 'Inter, sans-serif',
                        textAlign: displayLanguage === 'ar' ? 'right' : 'left'
                      }}>
                        <time>{formatDate(insight.published_at || insight.Date || insight.date)}</time>
                      </div>

                      {/* Title */}
                      <h2 style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '20px',
                        fontWeight: 700,
                        color: '#1a1a1a',
                        marginBottom: '12px',
                        lineHeight: 1.4,
                        textAlign: displayLanguage === 'ar' ? 'right' : 'left',
                        cursor: 'pointer',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {getTitle(insight)}
                      </h2>
                      
                      {/* Excerpt */}
                      <p style={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '15px',
                        color: '#555',
                        lineHeight: 1.6,
                        marginBottom: '20px',
                        textAlign: displayLanguage === 'ar' ? 'right' : 'left',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        flex: 1
                      }}>
                        {getExcerpt(insight)}
                      </p>
                      
                      {/* Read More Link */}
                      <div style={{
                        display: 'flex',
                        justifyContent: displayLanguage === 'ar' ? 'flex-start' : 'flex-end',
                        paddingTop: '15px',
                        borderTop: '1px solid #f0f0f0',
                        marginTop: 'auto'
                      }}>
                        <div 
                          style={{
                            color: '#1a1a1a',
                            fontWeight: '600',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            fontFamily: 'Inter, sans-serif'
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleInsightClick(insight);
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = '#000';
                            e.target.style.gap = '10px';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = '#1a1a1a';
                            e.target.style.gap = '6px';
                          }}
                        >
                          {displayLanguage === 'ar' ? 'اقرأ المزيد' : 'Read More'}
                          <svg 
                            width="14" 
                            height="14" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                            style={{
                              transform: displayLanguage === 'ar' ? 'scaleX(-1)' : 'none',
                              transition: 'transform 0.3s ease'
                            }}
                          >
                            <path d="M9 18l6-6-6-6"/>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '80px 20px',
                background: '#fff',
                borderRadius: '16px',
                color: '#666'
              }}>
                <h3 style={{
                  fontSize: '24px',
                  marginBottom: '15px',
                  fontFamily: 'Inter, sans-serif',
                  color: '#1a1a1a'
                }}>
                  {displayLanguage === 'ar' ? 'لا توجد منشورات' : 'No posts found'}
                </h3>
                <p style={{ fontSize: '16px' }}>
                  {displayLanguage === 'ar' ? 'جرب البحث بكلمات مختلفة أو اختر تصنيف آخر' : 'Try searching with different keywords or select another category'}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      <div style={{ marginTop: 'auto', paddingTop: '60px' }}>
        <Footer />
      </div>
      
      <style>{`
        /* Mobile responsiveness for blog */
        @media (max-width: 1024px) {
          .insights-blog-page section > div[style*="grid-template-columns"] {
            grid-template-columns: 1fr !important;
          }
          
          .insights-blog-page aside {
            position: relative !important;
            top: 0 !important;
            order: 1 !important;
          }
          
          .insights-blog-page aside > div {
            margin-bottom: 30px;
          }
        }
        
        @media (max-width: 768px) {
          .insights-blog-page section > div[style*="grid-template-columns"] {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
          
          .insights-blog-page section > div > div[style*="grid-template-columns"] {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
        }
        
        @media (max-width: 480px) {
          .insights-blog-page article > div:first-child {
            height: 200px !important;
          }
        }
      `}</style>
    </div>
  )
}

export default InsightsBlogPage
